import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface FormField {
  name: string;
  type: string;
  label: string;
  required: boolean;
}

interface Bucket {
  name: string;
  weight: number;
  to?: string;
}

interface AutomationFlow {
  id: string;
  name: string;
  trigger: string;
  status: 'draft' | 'published';
  nodes: Array<{
    id: string;
    label: string;
    type?: string;
    position: { x: number; y: number };
    tag?: string | string[];
    selectedTags?: string[];
    template?: string;
    variantId?: string;
    variables?: { name: string };
    delayType?: string;
    amount?: number;
    unit?: string;
    date?: string;
    time?: string;
    conditions?: Array<{ category: string; operator: string; value: string }>;
    maxWaitAmount?: number;
    maxWaitUnit?: string;
    buckets?: Bucket[];
  }>;
  edges: Array<{ id: string; source: string; target: string }>;
  timestamp: string;
  customFormUrl?: string;
  customFormFields?: FormField[];
}

interface AutomationState {
  automationFlowName: string;
  trigger: string;
  emailCampaign: string;
  appliesTo: string;
  description?: string;
  savedAutomations: AutomationFlow[];
  customFormUrl: string;
  customFormFields: FormField[];
  conditions: any[];
}

const initialState: AutomationState = {
  automationFlowName: 'untitled',
  trigger: '',
  emailCampaign: '',
  appliesTo: '',
  description: '',
  savedAutomations: [],
  customFormUrl: '',
  customFormFields: [],
  conditions: [],
};

const automationSlice = createSlice({
  name: 'automation',
  initialState,
  reducers: {
    setAutomationData: (state, action: PayloadAction<AutomationState>) => {
      return { ...state, ...action.payload };
    },
    updateAutomationData: (
      state,
      action: PayloadAction<Partial<AutomationState>>
    ) => {
      return { ...state, ...action.payload };
    },
    saveAutomation: (
      state,
      action: PayloadAction<{
        id?: string;
        name: string;
        trigger: string;
        status: 'draft' | 'published';
        nodes: Array<{
          id: string;
          label: string;
          type?: string;
          position: { x: number; y: number };
          tag?: string | string[];
          selectedTags?: string[];
          template?: string;
          variantId?: string;
          variables?: { name: string };
          delayType?: string;
          amount?: number;
          unit?: string;
          date?: string;
          time?: string;
          conditions?: Array<{
            category: string;
            operator: string;
            value: string;
          }>;
          maxWaitAmount?: number;
          maxWaitUnit?: string;
          buckets?: Bucket[];
        }>;
        edges: Array<{ id: string; source: string; target: string }>;
        customFormUrl?: string;
        customFormFields?: FormField[];
      }>
    ) => {
      const {
        id,
        name,
        trigger,
        status,
        nodes,
        edges,
        customFormUrl,
        customFormFields,
      } = action.payload;
      const now = new Date();
      const formattedDate = `${now.getDate().toString().padStart(2, '0')}-${(
        now.getMonth() + 1
      )
        .toString()
        .padStart(2, '0')}-${now.getFullYear()}`;

      const newAutomation: AutomationFlow = {
        id: id || Date.now().toString(),
        name,
        trigger,
        status,
        nodes: nodes.map((node) => ({
          ...node,
          conditions: node.conditions || [],
        })),
        edges,
        timestamp: formattedDate,
        customFormUrl,
        customFormFields,
      };

      const existingIndex = state.savedAutomations.findIndex(
        (a) => a.id === newAutomation.id
      );
      if (existingIndex !== -1) {
        state.savedAutomations[existingIndex] = newAutomation;
      } else {
        state.savedAutomations.push(newAutomation);
      }
    },
    resetAutomationData: (state) => {
      state.automationFlowName = 'untitled';
      state.trigger = '';
      state.emailCampaign = '';
      state.appliesTo = '';
      state.description = '';
      state.customFormUrl = '';
      state.customFormFields = [];
      state.conditions = [];
    },
  },
});

export const {
  setAutomationData,
  updateAutomationData,
  saveAutomation,
  resetAutomationData,
} = automationSlice.actions;
export default automationSlice.reducer;